/**
 * User Management Component - Admin interface for managing users
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Alert,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Menu,
  InputLabel,
  Select,
  FormControl,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from '@mui/material';
import {
  Refresh,
  Block,
  CheckCircle,
  Delete,
  Email,
  Search,
  MoreVert,
  AdminPanelSettings,
  Person,
  ContentCopy,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import { UserWithBatches } from '@/types';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { formatDateDefault } from '@/utils/dateUtils';

const UserManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();

  // State
  const [users, setUsers] = useState<UserWithBatches[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Pagination
  const [page] = useState(1);
  const [limit] = useState(50);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<boolean | null>(null);
  
  // Actions
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<UserWithBatches | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Load users
  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {
        page,
        limit,
      };
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      if (roleFilter) {
        params.role = roleFilter;
      }
      if (statusFilter !== null) {
        params.is_active = statusFilter;
      }
      
      const data = await apiService.listUsers(params);
      setUsers(data);
    } catch (err: any) {
      console.error('Failed to load users:', err);
      setError(err.detail || t('admin.users.load_error', 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [page, searchTerm, roleFilter, statusFilter]);

  // Handle actions menu
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: UserWithBatches) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  // Toggle user status
  const handleToggleStatus = async () => {
    if (!selectedUser) return;
    
    setActionLoading(true);
    try {
      await apiService.updateUserStatus(selectedUser.id, !selectedUser.is_active);
      setSuccess(
        t(
          'admin.users.status_updated',
          `User ${selectedUser.is_active ? 'deactivated' : 'activated'} successfully`
        )
      );
      handleMenuClose();
      loadUsers();
    } catch (err: any) {
      setError(err.detail || t('admin.users.status_update_error', 'Failed to update user status'));
    } finally {
      setActionLoading(false);
    }
  };

  // Reset password
  const handleResetPassword = async () => {
    if (!selectedUser) return;
    
    setActionLoading(true);
    try {
      const result = await apiService.resetUserPassword(selectedUser.id);
      if (result.token) {
        setTempPassword(result.token);
        setPasswordDialogOpen(true);
      } else {
        setSuccess(result.message || t('admin.users.password_reset_sent', 'Password reset email sent'));
      }
      handleMenuClose();
    } catch (err: any) {
      setError(err.detail || t('admin.users.password_reset_error', 'Failed to reset password'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyPassword = async () => {
    if (!tempPassword) return;
    try {
      await navigator.clipboard.writeText(tempPassword);
      setSuccess(t('admin.users.password_copied', 'Password copied to clipboard!'));
    } catch (err) {
      setError(t('admin.users.password_copy_error', 'Failed to copy password'));
    }
  };

  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
    setTempPassword(null);
    setShowPassword(false);
  };

  // Delete user
  const handleDeleteClick = () => {
    if (!selectedUser) return;
    setUserToDelete(selectedUser.id);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    setActionLoading(true);
    try {
      await apiService.deleteUser(userToDelete);
      setSuccess(t('admin.users.deleted', 'User deleted successfully'));
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      loadUsers();
    } catch (err: any) {
      setError(err.detail || t('admin.users.delete_error', 'Failed to delete user'));
    } finally {
      setActionLoading(false);
    }
  };
  
  // Promote/demote user role
  const handleToggleRole = async () => {
    if (!selectedUser) return;
    
    const newRole = selectedUser.role === 'admin' ? 'user' : 'admin';
    
    setActionLoading(true);
    try {
      await apiService.updateUserRole(selectedUser.id, newRole);
      setSuccess(
        t(
          'admin.users.role_updated',
          `User role updated to ${newRole} successfully`
        )
      );
      handleMenuClose();
      loadUsers();
    } catch (err: any) {
      setError(err.detail || t('admin.users.role_update_error', 'Failed to update user role'));
    } finally {
      setActionLoading(false);
    }
  };

  // Clear messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">
          {t('admin.users.title', 'User Management')}
        </Typography>
        <Button
          startIcon={<Refresh />}
          onClick={loadUsers}
          disabled={loading}
          variant="outlined"
        >
          {t('admin.users.refresh', 'Refresh')}
        </Button>
      </Box>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label={t('admin.users.search', 'Search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('admin.users.search_placeholder', 'Email, username, name...')}
            size="small"
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('admin.users.role', 'Role')}</InputLabel>
            <Select
              value={roleFilter}
              label={t('admin.users.role', 'Role')}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <MenuItem value="">{t('admin.users.all_roles', 'All')}</MenuItem>
              <MenuItem value="admin">{t('admin.users.admin', 'Admin')}</MenuItem>
              <MenuItem value="user">{t('admin.users.user', 'User')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('admin.users.status', 'Status')}</InputLabel>
            <Select
              value={statusFilter === null ? '' : statusFilter ? 'active' : 'inactive'}
              label={t('admin.users.status', 'Status')}
              onChange={(e) => {
                const value = e.target.value;
                setStatusFilter(value === '' ? null : value === 'active');
              }}
            >
              <MenuItem value="">{t('admin.users.all_statuses', 'All')}</MenuItem>
              <MenuItem value="active">{t('admin.users.active', 'Active')}</MenuItem>
              <MenuItem value="inactive">{t('admin.users.inactive', 'Inactive')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.users.email', 'Email')}</TableCell>
                <TableCell>{t('admin.users.username', 'Username')}</TableCell>
                <TableCell>{t('admin.users.full_name', 'Full Name')}</TableCell>
                <TableCell>{t('admin.users.user_id', 'User ID')}</TableCell>
                <TableCell>{t('admin.users.role', 'Role')}</TableCell>
                <TableCell>{t('admin.users.status', 'Status')}</TableCell>
                <TableCell>{t('admin.users.batches', 'Batches')}</TableCell>
                <TableCell>{t('admin.users.created', 'Created')}</TableCell>
                <TableCell>{t('admin.users.last_login', 'Last Login')}</TableCell>
                <TableCell align="right">{t('admin.users.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {t('admin.users.no_users', 'No users found')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.full_name || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontFamily: 'monospace', 
                            fontSize: '0.75rem',
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {user.id}
                        </Typography>
                        <Tooltip title={t('admin.users.copy_id', 'Copy User ID')}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(user.id);
                              setSuccess(t('admin.users.id_copied', 'User ID copied to clipboard!'));
                              setTimeout(() => setSuccess(null), 3000);
                            }}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role || 'user'}
                        color={user.role === 'admin' ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? t('admin.users.active', 'Active') : t('admin.users.inactive', 'Inactive')}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{user.batches_count || 0}</TableCell>
                    <TableCell>
                      {user.created_at ? formatDateDefault(user.created_at) : '-'}
                    </TableCell>
                    <TableCell>
                      {user.last_login ? formatDateDefault(user.last_login) : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('admin.users.actions', 'Actions')}>
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, user)}
                          disabled={user.id === currentUser?.id}
                          size="small"
                        >
                          <MoreVert />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem 
          onClick={handleToggleRole} 
          disabled={actionLoading || selectedUser?.id === currentUser?.id}
        >
          {selectedUser?.role === 'admin' ? (
            <>
              <Person sx={{ mr: 1 }} />
              {t('admin.users.remove_admin', 'Remove Admin Role')}
            </>
          ) : (
            <>
              <AdminPanelSettings sx={{ mr: 1 }} />
              {t('admin.users.promote_admin', 'Promote to Admin')}
            </>
          )}
        </MenuItem>
        <MenuItem onClick={handleToggleStatus} disabled={actionLoading || selectedUser?.id === currentUser?.id}>
          {selectedUser?.is_active ? (
            <>
              <Block sx={{ mr: 1 }} />
              {t('admin.users.deactivate', 'Deactivate')}
            </>
          ) : (
            <>
              <CheckCircle sx={{ mr: 1 }} />
              {t('admin.users.activate', 'Activate')}
            </>
          )}
        </MenuItem>
        <MenuItem onClick={handleResetPassword} disabled={actionLoading}>
          <Email sx={{ mr: 1 }} />
          {t('admin.users.reset_password', 'Reset Password')}
        </MenuItem>
        <MenuItem
          onClick={handleDeleteClick}
          disabled={actionLoading || selectedUser?.id === currentUser?.id}
          sx={{ color: 'error.main' }}
        >
          <Delete sx={{ mr: 1 }} />
          {t('admin.users.delete', 'Delete')}
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('admin.users.delete_confirm_title', 'Delete User')}
        message={t(
          'admin.users.delete_confirm_message',
          'Are you sure you want to delete this user? This will also delete all their batches and conversations. This action cannot be undone.'
        )}
        confirmText={t('admin.users.delete', 'Delete')}
        severity="error"
        isLoading={actionLoading}
      />

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onClose={handlePasswordDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('admin.users.password_reset_title', 'Password Reset Successful')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t(
              'admin.users.password_reset_warning',
              'Please securely communicate this temporary password to the user. They should change it after their first login.'
            )}
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('admin.users.temporary_password', 'Temporary Password')}:
          </Typography>
          <TextField
            fullWidth
            value={tempPassword || ''}
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                  <IconButton onClick={handleCopyPassword} edge="end" size="small">
                    <ContentCopy />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '1rem',
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {t('admin.users.password_reset_note', 'This password will not be shown again. Make sure to copy it now.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopyPassword} startIcon={<ContentCopy />}>
            {t('admin.users.copy_password', 'Copy Password')}
          </Button>
          <Button onClick={handlePasswordDialogClose} variant="contained">
            {t('common.close', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
